import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import toast from 'react-hot-toast';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  BookOpen, 
  Edit, 
  Download, 
  Palette, 
  Target, 
  Save, 
  Eye, 
  ArrowLeft,
  FileText,
  Image,
  BarChart3,
  DollarSign,
  CheckCircle,
  AlertCircle,
  Loader,
  Plus,
  Trash2,
  Copy,
  Rocket,
  Search,
  X,
  GripVertical
} from 'lucide-react';

const BookManagerPage = () => {
  const { bookId } = useParams();
  const navigate = useNavigate();
  const { user, API_BASE_URL } = useAuth();
  const [book, setBook] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generatingChapter, setGeneratingChapter] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [editingChapter, setEditingChapter] = useState(null);
  const [optimizationResult, setOptimizationResult] = useState(null);
  const [generatingCoverInManager, setGeneratingCoverInManager] = useState(false);
  const [showKDPModal, setShowKDPModal] = useState(false);
  const [downloadingPackage, setDownloadingPackage] = useState(false);
  const [showMetadataSelector, setShowMetadataSelector] = useState(false);
  const [availableMetadata, setAvailableMetadata] = useState([]);
  const [assigningMetadata, setAssigningMetadata] = useState(false);
  const [optimizingMetadata, setOptimizingMetadata] = useState(false);
  const [showFullMetadataModal, setShowFullMetadataModal] = useState(false);
  const [showCoverSelector, setShowCoverSelector] = useState(false);
  const [availableCovers, setAvailableCovers] = useState([]);
  const [loadingCovers, setLoadingCovers] = useState(false);
  const [assigningCover, setAssigningCover] = useState(false);
  
  // Insert chapter modal state
  const [showInsertModal, setShowInsertModal] = useState(false);
  const [insertAfterChapter, setInsertAfterChapter] = useState(null);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newChapterContent, setNewChapterContent] = useState('');
  const [insertingChapter, setInsertingChapter] = useState(false);
  const [generatingInsertContent, setGeneratingInsertContent] = useState(false);
  
  // Delete chapter state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [deletingChapter, setDeletingChapter] = useState(false);

  // Editable book data
  const [bookData, setBookData] = useState({
    title: '',
    genre: '',
    description: '',
    content: {}
  });

  useEffect(() => {
    if (bookId) {
      fetchBook();
    }
  }, [bookId]);

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const fetchBook = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const bookData = await response.json();
        setBook(bookData);
        setBookData({
          title: bookData.title,
          genre: bookData.genre,
          description: bookData.description || '',
          content: bookData.content || {}
        });
        
        // If book has existing metadata, set it as optimizationResult for display
        if (bookData.has_metadata && bookData.metadata) {
          // Ensure the metadata has the right structure for display
          const displayMetadata = {
            ...bookData.metadata,
            seo_score: bookData.metadata.seo_score || 8, // Default score if missing
            amazon_description: bookData.metadata.optimized_description || bookData.metadata.amazon_description
          };
          setOptimizationResult(displayMetadata);
        } else {
          setOptimizationResult(null);
        }
      } else {
        toast.error('Failed to load book');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Error fetching book:', error);
      toast.error('Failed to load book');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleChapterEdit = (chapterNum, content) => {
    setBookData(prev => ({
      ...prev,
      content: {
        ...prev.content,
        [chapterNum]: content
      }
    }));
  };

  const addNewChapter = async () => {
    setGeneratingChapter(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/add-chapter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(`Chapter ${data.chapter_number} added successfully! (${data.word_count} words)`);
        
        // Update local state with new chapter
        setBookData(prev => ({
          ...prev,
          content: {
            ...prev.content,
            [data.chapter_number]: data.chapter_content
          }
        }));
        
        // Refresh book data
        fetchBook();
      } else {
        throw new Error('Failed to add chapter');
      }
    } catch (error) {
      console.error('Error adding chapter:', error);
      toast.error('Failed to add new chapter');
    } finally {
      setGeneratingChapter(false);
    }
  };


  const handleGenerateInsertContent = async () => {
    if (!newChapterTitle.trim()) {
      toast.error('Please enter a chapter title first');
      return;
    }
    
    setGeneratingInsertContent(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/generate-insert-chapter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          after_chapter: insertAfterChapter,
          title: newChapterTitle
        })
      });

      if (response.ok) {
        const data = await response.json();
        setNewChapterContent(data.content);
        toast.success(`Generated ${data.word_count} words! Edit if needed, then click Insert.`);
        
        // Update user's credit balance
        if (updateUser && data.remaining_credits !== undefined) {
          updateUser({ credits: data.remaining_credits });
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to generate content');
      }
    } catch (error) {
      console.error('Error generating content:', error);
      toast.error('Failed to generate content');
    } finally {
      setGeneratingInsertContent(false);
    }
  };
  
  const handleInsertChapter = async () => {
    if (!newChapterTitle.trim()) {
      toast.error('Please enter a chapter title');
      return;
    }
    
    setInsertingChapter(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/insert-chapter`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          after_chapter: insertAfterChapter,
          title: newChapterTitle,
          content: newChapterContent
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Refresh the book data
        await fetchBook();
        
        // Close modal and reset
        setShowInsertModal(false);
        setNewChapterTitle('');
        setNewChapterContent('');
        setInsertAfterChapter(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to insert chapter');
      }
    } catch (error) {
      console.error('Error inserting chapter:', error);
      toast.error('Failed to insert chapter');
    } finally {
      setInsertingChapter(false);
    }
  };
  
  const handleDeleteChapter = async (chapterNum) => {
    setDeletingChapter(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/chapters/${chapterNum}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        
        // Refresh the book data
        await fetchBook();
        
        // Close confirmation modal
        setShowDeleteConfirm(null);
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete chapter');
      }
    } catch (error) {
      console.error('Error deleting chapter:', error);
      toast.error('Failed to delete chapter');
    } finally {
      setDeletingChapter(false);
    }
  };
  
  const handleDragEnd = async (result) => {
    if (!result.destination) return;
    
    const sourceIndex = result.source.index;
    const destIndex = result.destination.index;
    
    if (sourceIndex === destIndex) return;
    
    // Get chapter numbers in current order
    const chapterNums = Object.keys(bookData.content).sort((a, b) => parseInt(a) - parseInt(b));
    
    // Reorder the array
    const reordered = Array.from(chapterNums);
    const [removed] = reordered.splice(sourceIndex, 1);
    reordered.splice(destIndex, 0, removed);
    
    // Convert to actual chapter numbers for API
    const newOrder = reordered.map(num => parseInt(num));
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/reorder-chapters`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ new_order: newOrder })
      });

      if (response.ok) {
        toast.success('Chapters reordered successfully');
        
        // Refresh the book data
        await fetchBook();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reorder chapters');
      }
    } catch (error) {
      console.error('Error reordering chapters:', error);
      toast.error('Failed to reorder chapters');
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      
      // Prepare data to save - use bookData.content which includes chapter edits
      const saveData = {
        title: bookData.title,
        genre: bookData.genre,
        author_name: bookData.author_name,
        description: bookData.description,
        content: bookData.content
      };
      
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(saveData)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Book saved successfully!');
        
        // Refresh book data to show updated information
        await fetchBook();
      } else {
        throw new Error('Failed to save book');
      }
    } catch (error) {
      console.error('Error saving book:', error);
      toast.error('Failed to save book');
    } finally {
      setSaving(false);
    }
  };

  const generateCover = async () => {
    setGeneratingCoverInManager(true);
    try {
      const token = localStorage.getItem('token');
      
      // Ensure page_count is always a valid integer
      let pageCount = 100; // Default
      if (book.chapters && Array.isArray(book.chapters) && book.chapters.length > 0) {
        pageCount = Math.max(book.chapters.length * 15, 50);
      }
      
      // Prepare cover request with book data  
      const coverRequest = {
        title: book.title || 'Untitled Book',
        author_name: (bookData.author_name || book.author_name || 'Anonymous Author').trim(),
        genre: book.genre || 'Fiction',
        book_format: "paperback",
        dimensions: "6x9", 
        number_of_variations: 3,
        provider: 'fal',
        page_count: parseInt(pageCount, 10) // Ensure it's an integer
      };
      
      console.log('DEBUG: Cover request data:', coverRequest);
      
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/generate-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(coverRequest)
      });

      if (response.ok) {
        const result = await response.json();
        toast.success('Cover generated successfully!');
        fetchBook(); // Refresh book data
      } else {
        let errorMessage = 'Failed to generate cover';
        try {
          const errorData = await response.json();
          console.error('Cover generation error:', errorData);
          
          // Handle validation errors specifically
          if (response.status === 422) {
            if (errorData.detail && Array.isArray(errorData.detail)) {
              const fieldErrors = errorData.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ');
              errorMessage = `Validation error: ${fieldErrors}`;
            } else {
              errorMessage = `Validation error: ${errorData.detail || 'Invalid request data'}`;
            }
          } else {
            errorMessage = errorData.detail || errorData.message || `HTTP ${response.status}: ${response.statusText}`;
          }
        } catch (e) {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error generating cover:', error);
      
      // Handle different error types
      let displayMessage = 'Failed to generate cover';
      if (error.message && typeof error.message === 'string') {
        displayMessage = error.message;
      } else if (typeof error === 'string') {
        displayMessage = error;
      } else {
        displayMessage = 'Unknown error occurred';
        console.error('Unhandled error type:', typeof error, error);
      }
      
      toast.error(displayMessage);
    } finally {
      setGeneratingCoverInManager(false);
    }
  };

  const selectCover = async (coverIndex) => {
    try {
      const token = localStorage.getItem('token');
      
      // Call API to set the selected cover
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/select-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cover_index: coverIndex })
      });

      if (response.ok) {
        toast.success('Cover selected successfully!');
        fetchBook(); // Refresh book data to show selection
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to select cover');
      }
    } catch (error) {
      console.error('Error selecting cover:', error);
      toast.error(error.message || 'Failed to select cover');
    }
  };

  const loadAvailableCovers = async () => {
    setLoadingCovers(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/covers/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableCovers(data.covers || []);
      } else {
        throw new Error('Failed to load covers');
      }
    } catch (error) {
      console.error('Error loading covers:', error);
      toast.error('Failed to load available covers');
    } finally {
      setLoadingCovers(false);
    }
  };

  const assignCoverToBook = async (cover) => {
    setAssigningCover(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/assign-cover`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          cover_id: cover.id,
          cover_data: {
            image_base64: cover.image_base64,
            prompt: cover.prompt,
            provider: cover.provider || 'fal',
            width: cover.width,
            height: cover.height
          }
        })
      });

      if (response.ok) {
        toast.success('Cover assigned successfully!');
        setShowCoverSelector(false);
        // Refresh book data to show the new cover
        await fetchBook();
      } else {
        throw new Error('Failed to assign cover');
      }
    } catch (error) {
      console.error('Error assigning cover:', error);
      toast.error('Failed to assign cover to book');
    } finally {
      setAssigningCover(false);
    }
  };

  const openCoverSelector = () => {
    setShowCoverSelector(true);
    loadAvailableCovers();
  };

  const optimizeMetadata = async () => {
    setOptimizingMetadata(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/optimize-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setOptimizationResult(data.optimization);
        toast.success('Metadata optimized successfully!');
      } else {
        throw new Error('Failed to optimize metadata');
      }
    } catch (error) {
      console.error('Error optimizing metadata:', error);
      toast.error('Failed to optimize metadata');
    } finally {
      setOptimizingMetadata(false);
    }
  };

  const downloadKDPPackage = async () => {
    setDownloadingPackage(true);
    try {
      const token = localStorage.getItem('token');
      
      // Create metadata text file content
      const metadataContent = `AMAZON KDP METADATA - READY TO COPY & PASTE
============================================

Title: ${book.title}
Author: ${book.author || 'Your Name'}
Genre/Category: ${book.genre}
Description: ${book.description || 'No description available'}

${optimizationResult ? `
OPTIMIZED METADATA:
-------------------
SEO Score: ${optimizationResult.seo_score || 'N/A'}/10

Optimized Title: ${optimizationResult.optimized_title || optimizationResult.title || 'Not optimized'}

${optimizationResult.optimized_subtitle ? `Subtitle: ${optimizationResult.optimized_subtitle}

` : ''}Amazon Description:
${optimizationResult.optimized_description || optimizationResult.seo_description || optimizationResult.amazon_description || 'Not available'}

Keywords (use up to 7):
${(() => {
  const keywords = optimizationResult.recommended_keywords || optimizationResult.optimized_keywords || [];
  if (keywords.length === 0) return 'Not available';
  
  return keywords.slice(0, 7).map((kw, i) => {
    if (typeof kw === 'string') {
      return `${i + 1}. ${kw}`;
    } else if (kw.keyword) {
      return `${i + 1}. ${kw.keyword}${kw.monthly_searches ? ` (${kw.monthly_searches} searches/month)` : ''}`;
    } else {
      return `${i + 1}. ${kw}`;
    }
  }).join('\n');
})()}

Categories:
${(() => {
  const categories = optimizationResult.category_suggestions || optimizationResult.optimized_categories || [];
  if (categories.length === 0) return 'Not available';
  
  return categories.slice(0, 5).map((cat, i) => {
    if (typeof cat === 'string') {
      return `${i + 1}. ${cat}`;
    } else if (cat.category) {
      return `${i + 1}. ${cat.category}${cat.why_recommended ? ` - ${cat.why_recommended}` : ''}`;
    } else {
      return `${i + 1}. ${cat}`;
    }
  }).join('\n');
})()}

${optimizationResult.pricing_recommendations ? `
PRICING RECOMMENDATIONS:
------------------------
Launch Price: $${optimizationResult.pricing_recommendations.launch_price || 'Not specified'}
Promotional Price: $${optimizationResult.pricing_recommendations.promotional_price || 'Not specified'}
Optimal Range: $${optimizationResult.pricing_recommendations.optimal_range || 'Not specified'}
${optimizationResult.pricing_recommendations.reasoning ? `
Reasoning: ${optimizationResult.pricing_recommendations.reasoning}` : ''}

` : ''}${optimizationResult.optimization_tips && optimizationResult.optimization_tips.length > 0 ? `
OPTIMIZATION TIPS:
------------------
${optimizationResult.optimization_tips.map((tip, i) => `${i + 1}. ${tip}`).join('\n')}

` : ''}` : 'Metadata not optimized yet. Please optimize in the Metadata tab.'}

PUBLISHING CHECKLIST:
--------------------
☐ 1. Go to kdp.amazon.com and sign in
☐ 2. Click "Create a New Title" 
☐ 3. Choose "Kindle eBook" or "Paperback"
☐ 4. Enter your book details (copy from above)
☐ 5. Upload your manuscript (use the EPUB or DOCX file)
☐ 6. Upload your cover (use the cover image)
☐ 7. Set your price and royalty rate (70% recommended for $2.99+)
☐ 8. Preview your book
☐ 9. Publish!

Files included in this package:
- book.epub (for Kindle eBook)
- book.pdf (for paperback printing)  
- book.docx (for editing)
- cover.jpg (book cover image)
- metadata.txt (this file)
      `.trim();

      // Create and download metadata file
      const metadataBlob = new Blob([metadataContent], { type: 'text/plain' });
      const metadataUrl = URL.createObjectURL(metadataBlob);
      const metadataLink = document.createElement('a');
      metadataLink.href = metadataUrl;
      metadataLink.download = `${book.title.replace(/[^a-zA-Z0-9]/g, '_')}_KDP_Package_Metadata.txt`;
      metadataLink.click();
      URL.revokeObjectURL(metadataUrl);

      // Download book files
      const formats = ['epub', 'pdf', 'docx'];
      for (const format of formats) {
        try {
          await downloadBook(format);
          await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
        } catch (error) {
          console.error(`Error downloading ${format}:`, error);
        }
      }

      // Download cover if available
      if (book.covers && book.covers.length > 0 && book.covers[0].image_base64) {
        try {
          const coverData = book.covers[0].image_base64;
          const byteCharacters = atob(coverData);
          const byteNumbers = new Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
          }
          const byteArray = new Uint8Array(byteNumbers);
          const coverBlob = new Blob([byteArray], { type: 'image/jpeg' });
          
          const coverUrl = URL.createObjectURL(coverBlob);
          const coverLink = document.createElement('a');
          coverLink.href = coverUrl;
          coverLink.download = `${book.title.replace(/[^a-zA-Z0-9]/g, '_')}_Cover.jpg`;
          coverLink.click();
          URL.revokeObjectURL(coverUrl);
        } catch (error) {
          console.error('Error downloading cover:', error);
        }
      }

      toast.success('KDP package downloaded! Check your downloads folder.');
    } catch (error) {
      console.error('Error downloading KDP package:', error);
      toast.error('Failed to download KDP package');
    } finally {
      setDownloadingPackage(false);
    }
  };

  const openKDPGuide = () => {
    setShowKDPModal(true);
  };

  const loadAvailableMetadata = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/metadata/user`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Ensure data is always an array
        const metadataArray = Array.isArray(data) ? data : (data.metadata || []);
        setAvailableMetadata(metadataArray);
      } else {
        setAvailableMetadata([]);
      }
    } catch (error) {
      console.error('Error loading metadata:', error);
      setAvailableMetadata([]);
    }
  };

  const assignMetadataToBook = async (metadata) => {
    setAssigningMetadata(true);
    try {
      const token = localStorage.getItem('token');
      
      // Process keywords to extract strings from objects if needed
      const processedKeywords = metadata.optimized_keywords?.map(keyword => 
        typeof keyword === 'string' ? keyword : keyword.keyword || keyword
      ) || [];
      
      // Process categories to extract strings from objects if needed
      const processedCategories = metadata.optimized_categories?.map(category => 
        typeof category === 'string' ? category : category.category || category
      ) || [];
      
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/assign-metadata`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          metadata_id: metadata.id,
          metadata_data: {
            title: metadata.optimized_title,
            description: metadata.optimized_description,
            keywords: processedKeywords,
            categories: processedCategories,
            subtitle: metadata.optimized_subtitle
          }
        })
      });

      if (response.ok) {
        toast.success('Metadata assigned successfully!');
        setShowMetadataSelector(false);
        // Refresh book data to show the updated metadata
        await fetchBook();
        // Note: Don't set optimizationResult here - let fetchBook handle it
        // from the updated book data to ensure consistency
      } else {
        throw new Error('Failed to assign metadata');
      }
    } catch (error) {
      console.error('Error assigning metadata:', error);
      toast.error('Failed to assign metadata to book');
    } finally {
      setAssigningMetadata(false);
    }
  };

  const openMetadataSelector = () => {
    setShowMetadataSelector(true);
    loadAvailableMetadata();
  };
  const downloadBook = async (format) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/books/${bookId}/download/${format}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create blob and download
        const binaryString = atob(data.content);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: data.content_type });
        
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        
        toast.success(`${format.toUpperCase()} downloaded successfully!`);
      } else {
        throw new Error('Download failed');
      }
    } catch (error) {
      console.error('Error downloading:', error);
      toast.error('Failed to download book');
    }
  };


  const getPublishingReadiness = () => {
    const hasCover = book?.has_cover && (book?.selected_cover || (book?.covers && book?.covers.length > 0));
    const checks = [
      { name: 'Content Complete', status: book?.status === 'completed', icon: FileText },
      { name: 'Cover Selected', status: hasCover, icon: Image },
      { name: 'Metadata Optimized', status: !!optimizationResult || book?.has_metadata, icon: Target },
      { name: 'Ready for KDP', status: book?.status === 'completed' && hasCover && (!!optimizationResult || book?.has_metadata), icon: CheckCircle }
    ];

    const completedChecks = checks.filter(check => check.status).length;
    const readinessScore = (completedChecks / checks.length) * 100;

    return { checks, readinessScore };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-purple-400"></div>
        </div>
      </div>
    );
  }

  if (!book) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
        <Navbar />
        <div className="flex items-center justify-center min-h-[calc(100vh-64px)]">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-white mb-4">Book not found</h2>
            <Link to="/dashboard" className="text-purple-400 hover:text-purple-300">
              Return to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const { checks, readinessScore } = getPublishingReadiness();

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 to-indigo-900">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center space-x-4 mb-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-purple-400 hover:text-purple-300 transition-colors"
            >
              <ArrowLeft className="h-6 w-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-4xl font-bold text-white mb-2">{book.title}</h1>
              <p className="text-gray-300">
                {book.genre} • {book.chapters} chapters • {book.status}
              </p>
            </div>
            <button
              onClick={handleSave}
              disabled={saving || assigningMetadata}
              className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-6 py-3 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center space-x-2"
            >
              {(saving || assigningMetadata) ? <Loader className="h-5 w-5 animate-spin" /> : <Save className="h-5 w-5" />}
              <span>
                {saving || assigningMetadata 
                  ? 'Saving...' 
                  : 'Save Changes'
                }
              </span>
            </button>
          </div>

          {/* Publishing Readiness */}
          <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-white">KDP Publishing Readiness</h3>
              <div className="flex items-center space-x-2">
                <div className="text-2xl font-bold text-white">{Math.round(readinessScore)}%</div>
                <div className="w-20 h-2 bg-gray-700 rounded-full">
                  <div 
                    className="h-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full transition-all duration-500"
                    style={{ width: `${readinessScore}%` }}
                  />
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {checks.map((check, index) => (
                <div key={index} className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${check.status ? 'bg-green-500/20' : 'bg-gray-500/20'}`}>
                    <check.icon className={`h-5 w-5 ${check.status ? 'text-green-400' : 'text-gray-400'}`} />
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${check.status ? 'text-green-400' : 'text-gray-400'}`}>
                      {check.name}
                    </p>
                    <p className="text-xs text-gray-500">
                      {check.status ? 'Complete' : 'Pending'}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-8">
          <div className="flex space-x-1 bg-gray-900/50 p-1 rounded-2xl border border-purple-500/20">
            {[
              { id: 'content', label: 'Content', icon: FileText },
              { id: 'cover', label: 'Cover', icon: Image },
              { id: 'metadata', label: 'Metadata', icon: Target },
              { id: 'publish', label: 'Publish', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-xl font-medium transition-all flex items-center space-x-2 ${
                  activeTab === tab.id
                    ? 'bg-purple-600 text-white'
                    : 'text-gray-400 hover:text-white'
                }`}
              >
                <tab.icon className="h-5 w-5" />
                <span>{tab.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'content' && (
          <div className="space-y-6">
            {/* Book Info */}
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
              <h3 className="text-xl font-bold text-white mb-4">Book Information</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Title</label>
                  <input
                    type="text"
                    value={bookData.title}
                    onChange={(e) => setBookData(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Genre</label>
                  <select
                    value={bookData.genre}
                    onChange={(e) => setBookData(prev => ({ ...prev, genre: e.target.value }))}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                  >
                    <option value="">Select Genre</option>
                    <option value="Romance">Romance</option>
                    <option value="Thriller">Thriller</option>
                    <option value="Fantasy">Fantasy</option>
                    <option value="Science Fiction">Science Fiction</option>
                    <option value="Mystery">Mystery</option>
                    <option value="Horror">Horror</option>
                    <option value="Business">Business</option>
                    <option value="Self-Help">Self-Help</option>
                    <option value="Non-Fiction">Non-Fiction</option>
                    <option value="Biography">Biography</option>
                    <option value="History">History</option>
                    <option value="Adventure">Adventure</option>
                    <option value="Comedy">Comedy</option>
                    <option value="Drama">Drama</option>
                    <option value="Fiction">Fiction</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-300 mb-2">Description</label>
                <textarea
                  value={bookData.description}
                  onChange={(e) => setBookData(prev => ({ ...prev, description: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                  placeholder="Book description..."
                />
              </div>
            </div>

            {/* Chapters */}
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-bold text-white">Chapters</h3>
                <p className="text-sm text-gray-400">Use the + buttons between chapters to insert new chapters</p>
              </div>
              
              {generatingChapter && (
                <div className="mb-4 bg-blue-600/20 border border-blue-500/30 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-blue-300">
                    <Loader className="h-5 w-5 animate-spin" />
                    <span className="font-medium">AI is writing a new chapter...</span>
                  </div>
                  <p className="text-blue-200 text-sm mt-2">
                    The AI is analyzing your existing chapters and creating content that flows naturally with your story.
                  </p>
                </div>
              )}
              
              <div className="space-y-4">
                <DragDropContext onDragEnd={handleDragEnd}>
                  <Droppable droppableId="chapters">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="space-y-4">
                        {Object.keys(bookData.content).sort((a, b) => parseInt(a) - parseInt(b)).map((chapterNum, index) => {
                          // Handle both old format (string) and new format (object with title/text)
                          const chapterData = bookData.content[chapterNum];
                          const isNewFormat = typeof chapterData === 'object' && chapterData !== null && 'title' in chapterData && 'text' in chapterData;
                          const chapterTitle = isNewFormat ? chapterData.title : `Chapter ${chapterNum}`;
                          const chapterText = isNewFormat ? chapterData.text : (typeof chapterData === 'string' ? chapterData : '');
                          const wordCount = chapterText && typeof chapterText === 'string' ? chapterText.split(' ').length : 0;
                          
                          return (
                            <React.Fragment key={chapterNum}>
                              {/* Insert button before first chapter */}
                              {index === 0 && (
                                <button
                                  onClick={() => {
                                    setInsertAfterChapter(0);
                                    setShowInsertModal(true);
                                  }}
                                  className="w-full py-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-purple-500 hover:text-purple-400 transition-all flex items-center justify-center space-x-2"
                                >
                                  <Plus className="h-4 w-4" />
                                  <span>Insert chapter at beginning</span>
                                </button>
                              )}
                              
                              <Draggable draggableId={chapterNum} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    className={`bg-gray-800/50 rounded-lg p-4 border border-purple-500/10 ${
                                      snapshot.isDragging ? 'shadow-2xl shadow-purple-500/20' : ''
                                    }`}
                                  >
                                    <div className="flex items-center space-x-3">
                                      {/* Drag Handle */}
                                      <div {...provided.dragHandleProps} className="cursor-grab active:cursor-grabbing">
                                        <GripVertical className="h-5 w-5 text-gray-500 hover:text-purple-400" />
                                      </div>
                                      
                                      <div className="flex-1">
                                        <div className="flex items-center justify-between mb-3">
                                          <h4 className="text-lg font-semibold text-white">{chapterTitle}</h4>
                                          <div className="flex items-center space-x-2">
                                            <span className="text-xs text-gray-400 bg-gray-700 px-2 py-1 rounded">
                                              {wordCount} words
                                            </span>
                                            <button
                                              onClick={() => setEditingChapter(editingChapter === chapterNum ? null : chapterNum)}
                                              className="text-purple-400 hover:text-purple-300 transition-colors"
                                              title="Edit chapter"
                                            >
                                              <Edit className="h-5 w-5" />
                                            </button>
                                            <button
                                              onClick={() => setShowDeleteConfirm({ chapterNum, chapterTitle })}
                                              className="text-red-400 hover:text-red-300 transition-colors"
                                              title="Delete chapter"
                                              disabled={Object.keys(bookData.content).length === 1}
                                            >
                                              <Trash2 className="h-5 w-5" />
                                            </button>
                                          </div>
                                        </div>
                                        
                                        {editingChapter === chapterNum ? (
                                          <div className="space-y-3">
                        {/* Chapter Title Input */}
                        <div>
                          <label className="block text-sm font-medium text-gray-300 mb-2">
                            Chapter Title
                          </label>
                          <input
                            type="text"
                            value={chapterTitle}
                            onChange={(e) => {
                              const newData = isNewFormat ? { ...chapterData, title: e.target.value } : { title: e.target.value, text: chapterText };
                              handleChapterEdit(chapterNum, newData);
                            }}
                            className="w-full px-4 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                            placeholder="Enter chapter title"
                          />
                        </div>
                        
                            {/* Chapter Content Textarea */}
                            <div>
                              <label className="block text-sm font-medium text-gray-300 mb-2">
                                Chapter Content
                              </label>
                              <textarea
                                value={chapterText}
                                onChange={(e) => {
                                  const newData = isNewFormat ? { ...chapterData, text: e.target.value } : { title: chapterTitle, text: e.target.value };
                                  handleChapterEdit(chapterNum, newData);
                                }}
                                rows={10}
                                className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500"
                              />
                            </div>
                            
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => setEditingChapter(null)}
                                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={async () => {
                                  setEditingChapter(null);
                                  await handleSave();
                                }}
                                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                              >
                                Save
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-gray-300 leading-relaxed">
                            <p className="line-clamp-3">{chapterText}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </Draggable>
                              
                              {/* Insert button after each chapter */}
                              <button
                                onClick={() => {
                                  setInsertAfterChapter(parseInt(chapterNum));
                                  setShowInsertModal(true);
                                }}
                                className="w-full py-2 border-2 border-dashed border-gray-600 rounded-lg text-gray-400 hover:border-purple-500 hover:text-purple-400 transition-all flex items-center justify-center space-x-2"
                              >
                                <Plus className="h-4 w-4" />
                                <span>Insert chapter after Chapter {chapterNum}</span>
                              </button>
                            </React.Fragment>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
                
                {/* Bottom Add Chapter Button */}
                <div className="mt-8 border-2 border-dashed border-purple-500/30 rounded-lg p-8 text-center">
                  <div className="flex flex-col items-center space-y-4">
                    <div className="w-16 h-16 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-full flex items-center justify-center">
                      <Plus className="h-8 w-8 text-purple-400" />
                    </div>
                    
                    <div className="text-center">
                      <h4 className="text-lg font-semibold text-white mb-2">Continue Your Story</h4>
                      <p className="text-gray-400 text-sm mb-4">
                        AI will analyze your existing chapters and create content that flows naturally
                      </p>
                    </div>
                    
                    <button
                      onClick={addNewChapter}
                      disabled={generatingChapter || book?.status !== 'completed'}
                      className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-3 rounded-lg font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none flex items-center space-x-2"
                    >
                      {generatingChapter ? (
                        <>
                          <Loader className="h-5 w-5 animate-spin" />
                          <span>AI is writing your next chapter...</span>
                        </>
                      ) : (
                        <>
                          <Plus className="h-5 w-5" />
                          <span>Add New Chapter</span>
                        </>
                      )}
                    </button>
                    
                    {book?.status !== 'completed' && (
                      <p className="text-yellow-400 text-sm">
                        Complete your book generation first to add more chapters
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cover' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Book Cover</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={openCoverSelector}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <Image className="h-5 w-5" />
                    <span>Choose Cover</span>
                  </button>
                  <button
                    onClick={() => navigate('/kdp-covers')}
                    className="bg-gradient-to-r from-pink-600 to-purple-600 text-white px-6 py-3 rounded-lg font-medium hover:from-pink-700 hover:to-purple-700 transition-colors flex items-center space-x-2"
                  >
                    <Palette className="h-5 w-5" />
                    <span>Generate New Cover</span>
                  </button>
                </div>
              </div>

              {/* Current Cover Display */}
              {book.has_cover && (book.selected_cover || (book.covers && book.covers.length > 0)) ? (
                <div className="mb-6">
                  <h4 className="text-lg font-semibold text-white mb-4">Selected Cover</h4>
                  <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/20 max-w-sm">
                    <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden mb-4 flex items-center justify-center">
                      <img
                        src={`data:image/png;base64,${(book.selected_cover?.image_base64 || book.covers?.[0]?.image_base64)}`}
                        alt="Selected cover"
                        className="max-w-full max-h-full object-contain"
                        onError={(e) => {
                          console.error('Cover image failed to load:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <div className="flex items-center text-green-400 mb-2 text-sm">
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Current Selected Cover
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Image className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-400 mb-2">No Cover Selected</h4>
                  <p className="text-gray-500 mb-6">
                    Choose from your existing covers or generate new ones for your book.
                  </p>
                  <button
                    onClick={openCoverSelector}
                    className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-8 py-3 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-colors"
                  >
                    Choose Cover
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'metadata' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-white">Metadata Optimization</h3>
                <div className="flex space-x-3">
                  <button
                    onClick={openMetadataSelector}
                    className="bg-gradient-to-r from-green-600 to-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:from-green-700 hover:to-blue-700 transition-colors flex items-center space-x-2"
                  >
                    <Search className="h-5 w-5" />
                    <span>Select Metadata</span>
                  </button>
                  <button
                    onClick={optimizeMetadata}
                    disabled={optimizingMetadata}
                    className={`${
                      optimizationResult 
                        ? "bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700" 
                        : "bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                    } text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {optimizingMetadata ? (
                      <Loader className="h-5 w-5 animate-spin" />
                    ) : optimizationResult ? (
                      <CheckCircle className="h-5 w-5" />
                    ) : (
                      <Target className="h-5 w-5" />
                    )}
                    <span>
                      {optimizingMetadata 
                        ? 'Optimizing...' 
                        : optimizationResult 
                        ? 'Re-optimize Metadata' 
                        : 'Optimize Metadata'
                      }
                    </span>
                  </button>
                </div>
              </div>

              {optimizationResult ? (
                <div className="space-y-6">
                  <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 rounded-xl p-6 border border-purple-500/30">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-xl font-bold text-white">SEO Score</h4>
                      <div className="text-3xl font-bold text-white">{optimizationResult.seo_score}/10</div>
                    </div>
                    <div className="bg-gray-800/50 rounded-full h-3">
                      <div 
                        className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full"
                        style={{ width: `${optimizationResult.seo_score * 10}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Optimized Title</h4>
                      <p className="text-gray-300">{optimizationResult.optimized_title}</p>
                    </div>
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="font-semibold text-white mb-2">Subtitle</h4>
                      <p className="text-gray-300">{optimizationResult.optimized_subtitle}</p>
                    </div>
                  </div>

                  <div className="bg-gray-800/50 rounded-lg p-4">
                    <h4 className="font-semibold text-white mb-2">Amazon Description</h4>
                    <p className="text-gray-300 whitespace-pre-wrap">{optimizationResult.amazon_description}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h4 className="text-xl font-semibold text-gray-400 mb-2">No Optimization Available</h4>
                  <p className="text-gray-500 mb-6">Optimize your book's metadata for better discoverability</p>
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'publish' && (
          <div className="space-y-6">
            <div className="bg-gray-900/50 rounded-2xl p-8 border border-purple-500/20">
              <h2 className="text-2xl font-bold text-white text-center mb-8">
                Download & Publish
              </h2>
              
              {/* File Downloads */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FileText className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">PDF</h3>
                  <p className="text-gray-400 text-sm mb-4">Print-ready for paperback/hardcover</p>
                  <button 
                    onClick={() => downloadBook('pdf')}
                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download PDF</span>
                  </button>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">EPUB</h3>
                  <p className="text-gray-400 text-sm mb-4">Standard ebook format for Kindle</p>
                  <button 
                    onClick={() => downloadBook('epub')}
                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download EPUB</span>
                  </button>
                </div>
                
                <div className="text-center">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Edit className="h-8 w-8 text-white" />
                  </div>
                  <h3 className="text-lg font-semibold text-white mb-2">DOCX</h3>
                  <p className="text-gray-400 text-sm mb-4">Microsoft Word for editing</p>
                  <button 
                    onClick={() => downloadBook('docx')}
                    className="w-full bg-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                  >
                    <Download className="h-4 w-4" />
                    <span>Download DOCX</span>
                  </button>
                </div>
              </div>

              {/* Amazon KDP Publishing Section */}
              <div className="bg-gradient-to-r from-green-600/20 to-emerald-600/20 rounded-xl p-6 border border-green-500/30">
                <h3 className="text-xl font-bold text-white mb-4 text-center flex items-center justify-center space-x-2">
                  <Rocket className="h-6 w-6" />
                  <span>Ready for Amazon KDP?</span>
                </h3>
                <p className="text-gray-300 text-center mb-6">
                  Your book is {Math.round(readinessScore)}% ready for publishing on Amazon KDP. 
                  {readinessScore === 100 && " All requirements are complete!"}
                </p>
                
                {readinessScore === 100 ? (
                  <div className="space-y-4">
                    {/* Option 3: Smart Links Row */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <a 
                        href="https://kdp.amazon.com/en_US/bookshelf" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="bg-orange-600 text-white px-4 py-3 rounded-lg text-center font-semibold hover:bg-orange-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <span>🔗</span>
                        <span>Open Amazon KDP</span>
                      </a>
                      
                      <button 
                        onClick={downloadKDPPackage}
                        disabled={downloadingPackage}
                        className="bg-blue-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors flex items-center justify-center space-x-2 disabled:opacity-50"
                      >
                        <span>📦</span>
                        <span>{downloadingPackage ? 'Downloading...' : 'Download KDP Package'}</span>
                      </button>
                      
                      <button 
                        onClick={openKDPGuide}
                        className="bg-purple-600 text-white px-4 py-3 rounded-lg font-semibold hover:bg-purple-700 transition-colors flex items-center justify-center space-x-2"
                      >
                        <FileText className="h-4 w-4" />
                        <span>View Publishing Guide</span>
                      </button>
                    </div>
                    
                    {/* Option 2: Quick Access to Metadata */}
                    <div className="bg-gray-800/50 rounded-lg p-4">
                      <h4 className="text-white font-semibold mb-3 flex items-center space-x-2">
                        <Target className="h-5 w-5 text-green-400" />
                        <span>Your Optimized Metadata (Ready to Copy & Paste)</span>
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-400 mb-1">Title:</p>
                          <p className="text-white bg-gray-700 px-3 py-2 rounded">{book.title}</p>
                        </div>
                        <div>
                          <p className="text-gray-400 mb-1">Genre/Category:</p>
                          <p className="text-white bg-gray-700 px-3 py-2 rounded">{book.genre}</p>
                        </div>
                      </div>
                      <button 
                        onClick={() => setShowFullMetadataModal(true)}
                        className="mt-3 text-green-400 hover:text-green-300 text-sm font-medium flex items-center transition-colors"
                      >
                        <Edit className="h-4 w-4 mr-1" />
                        View Full Metadata →
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4 text-center">
                    <AlertCircle className="h-6 w-6 text-yellow-400 mx-auto mb-2" />
                    <p className="text-yellow-200 font-medium">
                      Complete remaining steps to publish
                    </p>
                    <div className="mt-3 flex flex-wrap justify-center gap-2">
                      {checks.map((check, index) => (
                        <span 
                          key={index}
                          className={`text-xs px-2 py-1 rounded-full ${
                            check.status 
                              ? 'bg-green-500/20 text-green-300' 
                              : 'bg-red-500/20 text-red-300'
                          }`}
                        >
                          {check.status ? '✓' : '✗'} {check.name}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Metadata Selector Modal */}
      {showMetadataSelector && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-purple-500/20 max-w-6xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-purple-500/20 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white">
                Select Metadata for "{book.title}"
              </h2>
              <button
                onClick={() => setShowMetadataSelector(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6">
              {Array.isArray(availableMetadata) && availableMetadata.length === 0 ? (
                <div className="text-center py-12">
                  <Target className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">No Metadata Available</h3>
                  <p className="text-gray-500 mb-6">
                    You haven't optimized any metadata yet. Create some optimized metadata first.
                  </p>
                  <button
                    onClick={() => {
                      setShowMetadataSelector(false);
                      navigate('/metadata-optimizer');
                    }}
                    className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                  >
                    Optimize Metadata
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.isArray(availableMetadata) && availableMetadata.map((metadata, index) => {
                    // Ensure metadata object has required properties
                    const safeMetadata = {
                      seo_score: metadata?.seo_score || 0,
                      optimized_title: metadata?.optimized_title || 'No title',
                      optimized_description: metadata?.optimized_description || metadata?.amazon_description || 'No description',
                      optimized_keywords: metadata?.optimized_keywords || metadata?.recommended_keywords || [],
                      optimized_categories: metadata?.optimized_categories || metadata?.category_suggestions || [],
                      created_at: metadata?.created_at || new Date().toISOString(),
                      id: metadata?.id || metadata?._id || index
                    };
                    
                    return (
                    <div key={index} className="bg-gray-800/50 rounded-lg p-6 border border-purple-500/10">
                      <div className="mb-4">
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-white font-semibold">SEO Score</h4>
                          <span className="text-2xl font-bold text-white">{safeMetadata.seo_score}/10</span>
                        </div>
                        <div className="bg-gray-700 rounded-full h-2">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-purple-500 h-2 rounded-full"
                            style={{ width: `${safeMetadata.seo_score * 10}%` }}
                          />
                        </div>
                      </div>
                      
                      {/* Optimized Title */}
                      <div className="mb-3">
                        <h4 className="text-blue-400 text-xs font-medium mb-1">OPTIMIZED TITLE:</h4>
                        <p className="text-white text-sm font-medium line-clamp-2">
                          {safeMetadata.optimized_title}
                        </p>
                      </div>
                      
                      {/* Description Preview */}
                      <div className="bg-gray-900/50 rounded p-3 mb-3">
                        <h4 className="text-blue-400 text-xs font-medium mb-1">DESCRIPTION:</h4>
                        <p className="text-gray-300 text-xs line-clamp-3">
                          {safeMetadata.optimized_description}
                        </p>
                      </div>
                      
                      {/* Keywords Preview */}
                      <div className="mb-3">
                        <h4 className="text-green-400 text-xs font-medium mb-1">KEYWORDS:</h4>
                        <div className="flex flex-wrap gap-1">
                          {safeMetadata.optimized_keywords?.slice(0, 3).map((keywordObj, idx) => (
                            <span key={idx} className="bg-green-500/20 text-green-300 px-2 py-0.5 rounded text-xs">
                              {typeof keywordObj === 'string' ? keywordObj : keywordObj.keyword || keywordObj}
                            </span>
                          ))}
                          {safeMetadata.optimized_keywords?.length > 3 && (
                            <span className="text-gray-400 text-xs">+{safeMetadata.optimized_keywords.length - 3} more</span>
                          )}
                        </div>
                      </div>
                      
                      {/* Categories Preview */}
                      {safeMetadata.optimized_categories?.length > 0 && (
                        <div className="mb-3">
                          <h4 className="text-purple-400 text-xs font-medium mb-1">CATEGORIES:</h4>
                          <div className="flex flex-wrap gap-1">
                            {safeMetadata.optimized_categories.slice(0, 2).map((categoryObj, idx) => (
                              <span key={idx} className="bg-purple-500/20 text-purple-300 px-2 py-0.5 rounded text-xs">
                                {typeof categoryObj === 'string' ? categoryObj : categoryObj.category || categoryObj}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Select Button */}
                      <button
                        onClick={() => assignMetadataToBook(metadata)}
                        disabled={assigningMetadata}
                        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-2 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {assigningMetadata ? (
                          <Loader className="h-4 w-4 animate-spin" />
                        ) : (
                          'Select This Metadata'
                        )}
                      </button>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* KDP Publishing Guide Modal */}
      {showKDPModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl border border-purple-500/20 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-900 border-b border-purple-500/20 p-6 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white flex items-center space-x-2">
                <FileText className="h-6 w-6" />
                <span>Amazon KDP Publishing Guide</span>
              </h2>
              <button
                onClick={() => setShowKDPModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Introduction */}
              <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-4">
                <h3 className="text-lg font-bold text-white mb-2 flex items-center">
                  <Rocket className="h-5 w-5 mr-2" />
                  Ready to Publish Your Book!
                </h3>
                <p className="text-blue-200">
                  Follow this step-by-step guide to publish your book on Amazon KDP. Your files are ready to upload!
                </p>
              </div>

              {/* Step-by-step guide */}
              <div className="space-y-4">
                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">1</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Set Up Your Amazon KDP Account</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• Go to <a href="https://kdp.amazon.com" target="_blank" className="text-orange-400 underline">kdp.amazon.com</a></li>
                      <li>• Sign in with your Amazon account (or create one)</li>
                      <li>• Complete your tax information and payment details</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">2</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Create Your Book</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• Click <strong>"+ Create a New Title"</strong></li>
                      <li>• Choose <strong>"Kindle eBook"</strong> (recommended) or <strong>"Paperback"</strong></li>
                      <li>• Select your primary marketplace (usually your country)</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">3</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Enter Book Details</h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="text-gray-400 text-xs">Title:</p>
                        <p className="text-white text-sm">{book.title}</p>
                      </div>
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="text-gray-400 text-xs">Author:</p>
                        <p className="text-white text-sm">{book.author || 'Your Name'}</p>
                      </div>
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="text-gray-400 text-xs">Genre:</p>
                        <p className="text-white text-sm">{book.genre}</p>
                      </div>
                      <div className="bg-gray-700/50 p-3 rounded">
                        <p className="text-gray-400 text-xs">Language:</p>
                        <p className="text-white text-sm">English</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">4</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Upload Your Manuscript</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• Use your <strong>EPUB file</strong> (recommended for eBooks)</li>
                      <li>• Or use <strong>DOCX file</strong> if you want Amazon to convert it</li>
                      <li>• For paperbacks, use the <strong>PDF file</strong></li>
                      <li>• Amazon will show you a preview - check it carefully!</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">5</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Upload Your Cover</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• Use your downloaded <strong>cover.jpg</strong> file</li>
                      <li>• Meets Amazon's requirements: 2560x1600px, 1.6:1 ratio</li>
                      <li>• Amazon will show a preview on different devices</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">6</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Add Your Optimized Metadata</h4>
                    {optimizationResult ? (
                      <div className="space-y-3">
                        {/* Optimized Title */}
                        {optimizationResult.optimized_title && (
                          <div className="bg-gray-700/50 p-3 rounded">
                            <p className="text-gray-400 text-xs">Optimized Title:</p>
                            <p className="text-white text-sm">{optimizationResult.optimized_title}</p>
                          </div>
                        )}
                        
                        {/* Subtitle */}
                        {optimizationResult.optimized_subtitle && (
                          <div className="bg-gray-700/50 p-3 rounded">
                            <p className="text-gray-400 text-xs">Subtitle:</p>
                            <p className="text-white text-sm">{optimizationResult.optimized_subtitle}</p>
                          </div>
                        )}
                        
                        {/* Book Description */}
                        <div className="bg-gray-700/50 p-3 rounded">
                          <p className="text-gray-400 text-xs">Book Description:</p>
                          <p className="text-white text-sm">
                            {(optimizationResult.optimized_description || 
                              optimizationResult.seo_description || 
                              optimizationResult.amazon_description)?.substring(0, 150)}...
                          </p>
                        </div>
                        
                        {/* Keywords */}
                        {((optimizationResult.recommended_keywords && optimizationResult.recommended_keywords.length > 0) || 
                          (optimizationResult.optimized_keywords && optimizationResult.optimized_keywords.length > 0)) && (
                          <div className="bg-gray-700/50 p-3 rounded">
                            <p className="text-gray-400 text-xs">Keywords (use up to 7):</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {(optimizationResult.recommended_keywords || optimizationResult.optimized_keywords || [])
                                .slice(0, 7).map((keyword, idx) => (
                                <span key={idx} className="bg-blue-600/20 text-blue-300 px-2 py-1 rounded text-xs">
                                  {typeof keyword === 'string' ? keyword : keyword.keyword || keyword}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Categories */}
                        {((optimizationResult.category_suggestions && optimizationResult.category_suggestions.length > 0) || 
                          (optimizationResult.optimized_categories && optimizationResult.optimized_categories.length > 0)) && (
                          <div className="bg-gray-700/50 p-3 rounded">
                            <p className="text-gray-400 text-xs">Categories:</p>
                            <div className="space-y-1 mt-1">
                              {(optimizationResult.category_suggestions || optimizationResult.optimized_categories || [])
                                .slice(0, 3).map((category, idx) => (
                                <span key={idx} className="block text-white text-sm">
                                  • {typeof category === 'string' ? category : category.category || category}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* SEO Score */}
                        {optimizationResult.seo_score && (
                          <div className="bg-gray-700/50 p-3 rounded">
                            <p className="text-gray-400 text-xs">SEO Score:</p>
                            <div className="flex items-center space-x-2 mt-1">
                              <span className="text-white font-bold">{optimizationResult.seo_score}/10</span>
                              <div className="flex-1 bg-gray-600 rounded-full h-2">
                                <div 
                                  className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                                  style={{ width: `${(optimizationResult.seo_score / 10) * 100}%` }}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <p className="text-yellow-300 text-sm">⚠️ Optimize your metadata first in the Metadata tab!</p>
                    )}
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">7</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Set Your Price & Royalties</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• <strong>Recommended:</strong> $2.99 - $9.99 for 70% royalty rate</li>
                      <li>• Below $2.99 = 35% royalty rate</li>
                      <li>• Consider your audience and genre pricing</li>
                      <li>• You can change prices anytime after publishing</li>
                    </ul>
                  </div>
                </div>

                <div className="flex items-start space-x-4 p-4 bg-gray-800/50 rounded-lg">
                  <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm">8</div>
                  <div className="flex-1">
                    <h4 className="font-bold text-white mb-2">Preview & Publish</h4>
                    <ul className="text-gray-300 space-y-1 text-sm">
                      <li>• Use Amazon's previewer to check your book</li>
                      <li>• Verify cover, formatting, and content</li>
                      <li>• Click <strong>"Publish Your Kindle eBook"</strong></li>
                      <li>• Your book will be live within 72 hours (usually much faster!)</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Success message */}
              <div className="bg-green-500/20 border border-green-500/30 rounded-lg p-4 text-center">
                <h4 className="text-lg font-bold text-white mb-2 flex items-center justify-center">
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Congratulations!
                </h4>
                <p className="text-green-200">
                  You're now ready to publish your book on Amazon KDP. Your files are optimized and ready to upload!
                </p>
                <div className="mt-4 flex flex-col sm:flex-row gap-3 justify-center">
                  <a
                    href="https://kdp.amazon.com/en_US/bookshelf"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-orange-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-orange-700 transition-colors"
                  >
                    Go to Amazon KDP →
                  </a>
                  <button
                    onClick={() => setShowKDPModal(false)}
                    className="bg-gray-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-gray-700 transition-colors"
                  >
                    Close Guide
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Full Metadata Modal */}
      {showFullMetadataModal && optimizationResult && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Full Metadata Details</h2>
                <p className="text-gray-400 mt-1">
                  Complete metadata optimization for {book?.title}
                </p>
              </div>
              <button
                onClick={() => setShowFullMetadataModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="space-y-8">
                
                {/* SEO Score */}
                <div className="bg-gradient-to-r from-purple-500/10 to-blue-500/10 rounded-lg p-6 border border-purple-500/20">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-white">SEO Score</h3>
                    <div className="text-3xl font-bold text-purple-400">
                      {optimizationResult?.seo_score || 0}/10
                    </div>
                  </div>
                  <div className="w-full bg-gray-700 rounded-full h-2">
                    <div 
                      className="bg-gradient-to-r from-purple-500 to-blue-500 h-2 rounded-full"
                      style={{ width: `${((optimizationResult?.seo_score || 0) / 10) * 100}%` }}
                    />
                  </div>
                </div>

                {/* Optimized Title */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3">Optimized Title</h3>
                  <p className="text-white text-lg leading-relaxed">
                    {optimizationResult?.optimized_title || 'No optimized title available'}
                  </p>
                  {optimizationResult?.optimized_subtitle && (
                    <p className="text-gray-400 text-sm mt-2">
                      Subtitle: {optimizationResult.optimized_subtitle}
                    </p>
                  )}
                </div>

                {/* Amazon Description */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3">Amazon Description</h3>
                  <div className="text-gray-300 leading-relaxed whitespace-pre-wrap">
                    {optimizationResult?.optimized_description || 
                     optimizationResult?.seo_description || 
                     optimizationResult?.amazon_description || 
                     'No description available'}
                  </div>
                </div>

                {/* Keywords */}
                {((optimizationResult?.recommended_keywords && optimizationResult.recommended_keywords.length > 0) || 
                  (optimizationResult?.optimized_keywords && optimizationResult.optimized_keywords.length > 0)) && (
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-3">Keywords</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {(optimizationResult.recommended_keywords || optimizationResult.optimized_keywords || []).slice(0, 6).map((keyword, index) => (
                        <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                          <p className="text-white font-medium">
                            {typeof keyword === 'string' ? keyword : keyword.keyword || keyword}
                          </p>
                          {keyword.monthly_searches && (
                            <p className="text-gray-400 text-sm">
                              {keyword.monthly_searches} monthly searches • {keyword.search_difficulty} difficulty
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Categories */}
                {((optimizationResult?.category_suggestions && optimizationResult.category_suggestions.length > 0) || 
                  (optimizationResult?.optimized_categories && optimizationResult.optimized_categories.length > 0)) && (
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-3">Categories</h3>
                    <div className="space-y-3">
                      {(optimizationResult.category_suggestions || optimizationResult.optimized_categories || []).slice(0, 3).map((category, index) => (
                        <div key={index} className="bg-gray-700/50 p-3 rounded-lg">
                          <p className="text-white font-medium">
                            {typeof category === 'string' ? category : category.category || category}
                          </p>
                          {category.why_recommended && (
                            <p className="text-gray-400 text-sm mt-1">
                              {category.why_recommended}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Additional Metadata Information */}
                <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                  <h3 className="text-lg font-semibold text-white mb-3">Additional Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {optimizationResult?.assigned_at && (
                      <div>
                        <p className="text-gray-400 text-sm">Assigned Date</p>
                        <p className="text-white">{new Date(optimizationResult.assigned_at).toLocaleDateString()}</p>
                      </div>
                    )}
                    {optimizationResult?.source_optimization_id && (
                      <div>
                        <p className="text-gray-400 text-sm">Source Optimization ID</p>
                        <p className="text-white text-xs">{optimizationResult.source_optimization_id}</p>
                      </div>
                    )}
                    {book?.has_metadata && (
                      <div>
                        <p className="text-gray-400 text-sm">Status</p>
                        <p className="text-green-400">✓ Assigned to Book</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Pricing Recommendations */}
                {optimizationResult?.pricing_recommendations && (
                  <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-600">
                    <h3 className="text-lg font-semibold text-white mb-3">Pricing Recommendations</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="text-center">
                        <p className="text-gray-400 text-sm">Launch Price</p>
                        <p className="text-white text-xl font-bold">${optimizationResult.pricing_recommendations.launch_price}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-400 text-sm">Promotional Price</p>
                        <p className="text-white text-xl font-bold">${optimizationResult.pricing_recommendations.promotional_price}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-gray-400 text-sm">Optimal Range</p>
                        <p className="text-white text-xl font-bold">${optimizationResult.pricing_recommendations.optimal_range}</p>
                      </div>
                    </div>
                    {optimizationResult.pricing_recommendations.reasoning && (
                      <p className="text-gray-300 text-sm mt-4 leading-relaxed">
                        {optimizationResult.pricing_recommendations.reasoning}
                      </p>
                    )}
                  </div>
                )}

              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cover Selector Modal */}
      {showCoverSelector && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-900 rounded-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-700 flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-white">Select Cover for "{book?.title}"</h2>
                <p className="text-gray-400 mt-1">Choose from your generated covers</p>
              </div>
              <button
                onClick={() => setShowCoverSelector(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {loadingCovers ? (
                <div className="flex items-center justify-center py-12">
                  <Loader className="h-8 w-8 text-purple-400 animate-spin" />
                  <span className="ml-2 text-gray-400">Loading covers...</span>
                </div>
              ) : availableCovers.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                  {availableCovers.map((cover, index) => (
                    <div key={`${cover.id}-${index}`} className="group relative">
                      <div className="aspect-[3/4] bg-gray-800 rounded-lg overflow-hidden border border-gray-600 hover:border-purple-500 transition-colors flex items-center justify-center">
                        <img
                          src={`data:image/png;base64,${cover.image_base64}`}
                          alt={`Cover for ${cover.title}`}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            console.error('Cover image failed to load');
                            e.target.style.display = 'none';
                          }}
                        />
                      </div>
                      
                      {/* Cover Info */}
                      <div className="mt-3">
                        <h3 className="text-white font-semibold text-sm mb-1 line-clamp-1">
                          {cover.title}
                        </h3>
                        <p className="text-gray-400 text-xs mb-1">
                          {cover.genre} • {new Date(cover.created_at).toLocaleDateString()}
                        </p>
                        {cover.dimensions && (
                          <p className="text-blue-400 text-xs mb-2">
                            {cover.book_format === 'paperback' ? `KDP ${cover.dimensions}` : `${cover.dimensions} px`}
                          </p>
                        )}
                        
                        <div className="flex space-x-2">
                          <button
                            onClick={() => assignCoverToBook(cover)}
                            disabled={assigningCover}
                            className="flex-1 bg-gradient-to-r from-pink-600 to-purple-600 text-white py-2 px-4 rounded-lg font-medium hover:from-pink-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                          >
                            {assigningCover ? (
                              <Loader className="h-4 w-4 animate-spin" />
                            ) : (
                              'Select This Cover'
                            )}
                          </button>
                        </div>
                      </div>
                      
                      {/* Cover Badge */}
                      <div className="absolute top-2 left-2 z-10">
                        <span className="inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium border bg-blue-500/20 text-blue-300 border-blue-500/30">
                          <Image className="h-3 w-3" />
                          <span>{cover.cover_type || 'Cover'}</span>
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-12">
                  <Palette className="h-16 w-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">
                    No covers available
                  </h3>
                  <p className="text-gray-500 mb-6">
                    Generate some covers first using the Cover Generator
                  </p>
                  <button
                    onClick={() => {
                      setShowCoverSelector(false);
                      navigate('/kdp-covers');
                    }}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 text-white px-6 py-3 rounded-lg font-medium hover:from-purple-700 hover:to-pink-700 transition-colors"
                  >
                    Generate Covers
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
      
      {/* Insert Chapter Modal */}
      {showInsertModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 max-w-2xl w-full border border-purple-500/20 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-white">
                Insert Chapter {insertAfterChapter === 0 ? 'at Beginning' : `After Chapter ${insertAfterChapter}`}
              </h2>
              <button
                onClick={() => {
                  setShowInsertModal(false);
                  setNewChapterTitle('');
                  setNewChapterContent('');
                }}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Chapter Title *
                </label>
                <input
                  type="text"
                  value={newChapterTitle}
                  onChange={(e) => setNewChapterTitle(e.target.value)}
                  placeholder="Enter chapter title"
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-300">
                    Chapter Content
                  </label>
                  <button
                    onClick={handleGenerateInsertContent}
                    disabled={generatingInsertContent || !newChapterTitle.trim()}
                    className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white text-sm rounded-lg hover:from-blue-700 hover:to-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                  >
                    {generatingInsertContent ? (
                      <>
                        <Loader className="h-4 w-4 animate-spin" />
                        <span>Generating...</span>
                      </>
                    ) : (
                      <>
                        <Rocket className="h-4 w-4" />
                        <span>Generate with AI</span>
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={newChapterContent}
                  onChange={(e) => setNewChapterContent(e.target.value)}
                  placeholder={generatingInsertContent ? "AI is writing your chapter..." : "Enter chapter content manually or click 'Generate with AI'"}
                  rows={10}
                  className="w-full px-4 py-3 bg-gray-800 border border-gray-600 rounded-lg text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  disabled={generatingInsertContent}
                />
                {newChapterContent && (
                  <p className="text-xs text-gray-400 mt-1">
                    {newChapterContent.split(' ').length} words • You can edit the AI-generated content before inserting
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  onClick={() => {
                    setShowInsertModal(false);
                    setNewChapterTitle('');
                    setNewChapterContent('');
                  }}
                  className="px-6 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleInsertChapter}
                  disabled={insertingChapter || !newChapterTitle.trim()}
                  className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
                >
                  {insertingChapter ? (
                    <>
                      <Loader className="h-4 w-4 animate-spin" />
                      <span>Inserting...</span>
                    </>
                  ) : (
                    <>
                      <Plus className="h-4 w-4" />
                      <span>Insert Chapter</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Chapter Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-xl p-6 max-w-md w-full border border-red-500/20 shadow-2xl">
            <div className="flex items-start space-x-4 mb-6">
              <div className="p-3 bg-red-500/20 rounded-lg">
                <Trash2 className="h-6 w-6 text-red-400" />
              </div>
              <div className="flex-1">
                <h2 className="text-xl font-bold text-white mb-2">Delete Chapter?</h2>
                <p className="text-gray-300 text-sm">
                  Are you sure you want to delete <span className="font-semibold text-white">Chapter {showDeleteConfirm.chapterNum}: {showDeleteConfirm.chapterTitle}</span>?
                </p>
                <p className="text-gray-400 text-xs mt-2">
                  This action cannot be undone. All subsequent chapters will be renumbered.
                </p>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(null)}
                disabled={deletingChapter}
                className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteChapter(showDeleteConfirm.chapterNum)}
                disabled={deletingChapter}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center space-x-2"
              >
                {deletingChapter ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Chapter</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookManagerPage;